import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Activity, ListTree, Shuffle } from 'lucide-react';

import AccountPlanManager from '../AccountPlanManager';
import ChartRolesSection from '../ChartRolesSection';
import ChartHealthSection from '../ChartHealthSection';

// Kontoplan — the chart, its semantic roles and its health, as three views of
// one object rather than three cards stacked on the Bokföring page. Stacked,
// they were 826px + 6 743px + 16 599px; only one is ever the thing you came
// for, and the other two were 30 000 DOM nodes in the way.
//
// The tab lives in ?vy= so a deep link (and the needs-mapping links from the
// bank and VAT flows, which point at the roles view) survives a reload. The
// health view's fiscal year lives in ?ar= for the same reason: the import
// result links here for the year it imported (independent review 2026-09-05,
// recommendation 3), which is not necessarily the newest year the backend
// lists first.

// Icons because the three views are different *kinds* of thing, not states of
// one — and because as three small grey words in a pill this switcher was easy
// to miss entirely.
const TABS = [
  { key: 'konton', label: 'Konton', icon: ListTree, hint: 'Alla konton i kontoplanen' },
  { key: 'roller', label: 'Roller', icon: Shuffle, hint: 'Vilka konton bokföringen använder som standard' },
  { key: 'halsa', label: 'Hälsa', icon: Activity, hint: 'Anmärkningar på kontoplanen' },
];

const DEFAULT_TAB = 'konton';

const ChartSection = ({ companyId, companyType, fiscalYears }) => {
  const history = useHistory();
  const location = useLocation();

  const search = new URLSearchParams(location.search);
  const requested = search.get('vy');
  const active = TABS.some((t) => t.key === requested) ? requested : DEFAULT_TAB;
  const years = Array.isArray(fiscalYears) ? fiscalYears : [];
  const requestedYear = search.get('ar');
  const healthYear = years.find((fy) => String(fy.fiscal_year_id) === String(requestedYear)) || years[0] || null;

  const replaceQuery = (mutate) => {
    const params = new URLSearchParams(location.search);
    mutate(params);
    const query = params.toString();
    history.replace(`${location.pathname}${query ? `?${query}` : ''}`);
  };
  const selectTab = (key) => replaceQuery((params) => {
    if (key === DEFAULT_TAB) params.delete('vy');
    else params.set('vy', key);
  });
  const selectHealthYear = (fiscalYearId) => replaceQuery((params) => {
    if (fiscalYearId) params.set('ar', fiscalYearId);
    else params.delete('ar');
  });
  return (
    <div id="kontoplan">
      <div className="ll-segmented settings-tabs" role="tablist" aria-label="Kontoplan">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active === tab.key}
              title={tab.hint}
              className={`ll-segmented__item${active === tab.key ? ' is-active' : ''}`}
              onClick={() => selectTab(tab.key)}
            >
              <Icon size={14} aria-hidden="true" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Each view mounts only when selected — that is the point of the split,
          so the chart's 1 247 rows are not in the DOM while you read the
          health findings. */}
      {active === 'konton' && (
        <AccountPlanManager companyId={companyId} companyType={companyType} />
      )}

      {active === 'roller' && <ChartRolesSection companyId={companyId} />}

      {active === 'halsa' && (
        // The year and the momsperiod are one context row inside the card
        // (UX_PLAN.md P3); the section owns both selectors.
        <ChartHealthSection
          companyId={companyId}
          fiscalYearId={healthYear?.fiscal_year_id || null}
          fiscalYears={years}
          onSelectFiscalYear={selectHealthYear}
        />
      )}
    </div>
  );
};

export default ChartSection;
